import React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import Collapse from '@mui/material/Collapse';
import { SvgIcon } from '@mui/material';
import { ReactComponent as ChevronTopIcon } from '../icons/chevron--top.svg';
import { ReactComponent as ChevronDownIcon } from '../icons/chevron--down.svg';
import { INavigationItem } from './NavigationItems';
import { useLocation } from "react-router-dom";
import { useAuth } from '../hooks/useAuth';
import { Status } from '../hooks/useFetch';
import { SourceMarketFields } from '@aviation/catering-masterdata-sdk';

export interface INavigationItemProps extends INavigationItem {
    sourceMarket?: SourceMarketFields;
    sourceMarketStatus?: Status;
}

function NavigationItem(props: INavigationItemProps) {
    const { icon, label, children, path, rootPath, sourceMarket, sourceMarketStatus } = props;
    const location = useLocation();
    const fullPath = path ? `${rootPath}/${path}` : rootPath;
    const selected = location.pathname.startsWith(fullPath);
    const auth = useAuth();

    // While the source market (and its feature flags) hasn't loaded yet, we don't know
    // whether flag-dependent items should show, so avoid flashing the wrong ones.
    const isLoadingFlags = sourceMarketStatus !== undefined && sourceMarketStatus !== Status.Fetched;

    const isVisible = (item : INavigationItem) => {
        let result = ((item.administration ?? false) === false && (item.superadmin ?? false) === false) || ((item.administration ?? true) && auth.isAdministrator && (item.superadmin ?? false) === false)
        || auth.isSuperAdmin;

        const isFlagDependent = !!item.featureFlag || !!item.hiddenByFeatureFlag;

        if(isFlagDependent) {
            // Visibility isn't known yet, so hide rather than flash the wrong item.
            if(isLoadingFlags)
                return false;

            if(item.featureFlag)
                result = result && (sourceMarket as any)?.[item.featureFlag] === true;

            if(item.hiddenByFeatureFlag)
                result = result && (sourceMarket as any)?.[item.hiddenByFeatureFlag] !== true;
        }

        return result;
    }

    return (
        <>
            <ListItemButton href={fullPath} selected={location.pathname === fullPath}>
                {icon ? <ListItemIcon>
                    <SvgIcon component={icon} inheritViewBox />
                </ListItemIcon> : null}
                <ListItemText primary={label} />
                {children ? (selected ?
                    <SvgIcon component={ChevronTopIcon} inheritViewBox /> :
                    <SvgIcon component={ChevronDownIcon} inheritViewBox />
                ) : null}
            </ListItemButton>
            {children ? <Collapse in={selected} timeout="auto" unmountOnExit>
                <List component="div" disablePadding dense>
                    {children.filter(o => isVisible(o)).map(({ id, label, path }) => {
                        const childPath = fullPath + path;
                        return (
                            <ListItemButton href={childPath} key={id} sx={{ pl: 8 }} selected={location.pathname === childPath}>
                                <ListItemText primary={label} />
                            </ListItemButton>
                        )
                    })}
                </List>
            </Collapse> : null}
        </>
    );
}

export default NavigationItem;